import { Autocomplete } from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import { r, s } from '@/theme/tokens';
import { Box, Text } from '@/ui/primitives';
import { Checkbox, CloseIcon, TextField } from '@/ui/primitives';
import { filterInputSx } from './filterControlStyles';
import type { ReactElement } from 'react';

type TagOption = { value: string; label: string };

type TagsFilterFieldProps = {
  value: string[];
  options: TagOption[];
  label?: string;
  labelPlacement?: 'inline' | 'stacked';
  labelControlGap?: string;
  inputAriaLabel?: string;
  emptyLabel?: string;
  fullWidth?: boolean;
  controlWidthPx?: number;
  inputBackgroundColor?: 'background.paper' | 'background.default';
  inputLeftPadding?: string;
  onChange: (next: string[]) => void;
};

export const TagsFilterField = ({
  value,
  options,
  label = 'Tags',
  labelPlacement = 'inline',
  labelControlGap,
  emptyLabel = 'All Tags',
  inputAriaLabel,
  fullWidth = false,
  controlWidthPx,
  inputBackgroundColor = 'background.paper',
  inputLeftPadding = s('sm'),
  onChange,
}: TagsFilterFieldProps): ReactElement => {
  const controlRef = useRef<HTMLDivElement | null>(null);
  const emptyOptionValue = '__flowqa_no_options__';
  const effectiveOptions =
    options.length > 0 ? options : [{ value: emptyOptionValue, label: 'No options' }];
  const selectedOptions = options.filter((option) => value.includes(option.value));
  const hasValue = selectedOptions.length > 0;
  const inputLeftInset = selectedOptions.length === 0 ? inputLeftPadding : s('none');
  const fixedFilterWidth = `calc(${s('5xl')} + ${s('2xl')})`;
  const resolvedControlWidth = controlWidthPx ? `${controlWidthPx}px` : fixedFilterWidth;
  const optionRowMinHeight = `calc(${s('2xl')} + ${s('xl')} + ${s('sm')})`;
  const optionHorizontalPadding = s('md');
  const optionCheckboxSlotWidth = `calc(${s('lg')} + ${s('sm')})`;
  const controlHeight = 32;
  const controlWidthValuePx = controlWidthPx ?? 180;
  const endAdornmentPadding =
    selectedOptions.length > 0 ? s('2xl') : `calc(${s('2xl')} + ${s('xxs')})`;
  const isInlineLabel = labelPlacement === 'inline';
  const [popperPlacement, setPopperPlacement] = useState<'bottom-start' | 'bottom-end'>(
    'bottom-start',
  );
  const estimatedDropdownWidth = Math.min(
    Math.max(
      controlWidthValuePx,
      options.reduce((maxWidth, option) => Math.max(maxWidth, option.label.length * 13 + 88), 0),
    ),
    640,
  );

  useEffect(() => {
    const updatePlacement = (): void => {
      if (typeof window === 'undefined') {
        return;
      }
      const rect = controlRef.current?.getBoundingClientRect();
      if (!rect) {
        return;
      }
      const viewportPadding = 16;
      const rightSpace = window.innerWidth - rect.left - viewportPadding;
      const leftSpace = rect.right - viewportPadding;
      setPopperPlacement(
        rightSpace < estimatedDropdownWidth && leftSpace > rightSpace
          ? 'bottom-end'
          : 'bottom-start',
      );
    };

    updatePlacement();
    window.addEventListener('resize', updatePlacement);
    return () => {
      window.removeEventListener('resize', updatePlacement);
    };
  }, [estimatedDropdownWidth]);

  const heightSx = {
    '& .MuiAutocomplete-inputRoot': {
      minHeight: `${controlHeight}px !important`,
      height: `${controlHeight}px !important`,
      maxHeight: `${controlHeight}px !important`,
      backgroundColor: inputBackgroundColor,
      alignItems: 'center',
      flexWrap: 'nowrap !important',
      overflow: 'hidden',
      paddingTop: `${s('none')} !important`,
      paddingBottom: `${s('none')} !important`,
      paddingRight: `${endAdornmentPadding} !important`,
      minWidth: 0,
      cursor: 'pointer',
      '& .MuiAutocomplete-endAdornment': {
        right: s('none'),
        display: 'flex',
        alignItems: 'center',
        gap: s('none'),
        '& > button + button': {
          marginLeft: `calc(${s('xxs')} - ${s('md')}) !important`,
        },
      },
      '& .MuiAutocomplete-clearIndicator': {
        margin: `${s('none')} !important`,
        marginRight: `${s('none')} !important`,
        padding: `${s('xxs')} !important`,
        width: 'auto',
        height: 'auto',
        opacity: 1,
        visibility: 'visible',
        pointerEvents: 'auto',
      },
      '& .MuiAutocomplete-popupIndicator': {
        padding: `${s('xxs')} !important`,
        margin: `${s('none')} !important`,
        marginRight: `${s('none')} !important`,
        width: 'auto',
        height: 'auto',
      },
    },
    '& .MuiAutocomplete-input': {
      minHeight: `${controlHeight}px !important`,
      height: `${controlHeight}px !important`,
      minWidth: hasValue ? '2ch !important' : undefined,
      width: hasValue ? '2ch !important' : 'auto !important',
      flex: hasValue ? '0 0 2ch' : '1 1 auto',
      paddingTop: `${s('none')} !important`,
      paddingBottom: `${s('none')} !important`,
      paddingLeft: `${inputLeftInset} !important`,
      marginLeft: `${s('none')} !important`,
      lineHeight: '1 !important',
      cursor: 'pointer',
    },
  } as const;

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: isInlineLabel ? 'row' : 'column',
        alignItems: isInlineLabel ? 'center' : 'stretch',
        gap: isInlineLabel ? (labelControlGap ?? s('sm')) : s('xs'),
        minWidth: 0,
        width: fullWidth ? '100%' : 'auto',
      }}
    >
      <Text
        role="label"
        color="textSecondary"
        sx={{ whiteSpace: isInlineLabel ? 'nowrap' : 'normal' }}
      >
        {label}
      </Text>
      <Box
        ref={controlRef}
        sx={{
          width: fullWidth ? '100%' : resolvedControlWidth,
          minWidth: fullWidth ? 0 : resolvedControlWidth,
          maxWidth: fullWidth ? 'none' : resolvedControlWidth,
          flex: fullWidth ? '1 1 0' : `0 0 ${resolvedControlWidth}`,
        }}
      >
        <Autocomplete
          multiple
          disableCloseOnSelect
          fullWidth={false}
          clearIcon={<CloseIcon fontSize="small" />}
          options={effectiveOptions}
          value={selectedOptions}
          isOptionEqualToValue={(option, selectedOption) => option.value === selectedOption.value}
          getOptionLabel={(option) => option.label}
          getOptionDisabled={(option) => option.value === emptyOptionValue}
          onChange={(_event, nextOptions) => {
            onChange(
              nextOptions
                .map((option) => option.value)
                .filter((optionValue) => optionValue !== emptyOptionValue),
            );
          }}
          renderOption={(props, option, { selected }) => {
            const { key, ...optionProps } = props;
            const isEmptyOption = option.value === emptyOptionValue;
            return (
              <Box
                component="li"
                key={key}
                {...optionProps}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: s('sm'),
                  px: s('none'),
                  py: s('md'),
                  minHeight: optionRowMinHeight,
                }}
              >
                {isEmptyOption ? (
                  <Box
                    aria-hidden="true"
                    sx={{ width: optionCheckboxSlotWidth, minWidth: optionCheckboxSlotWidth }}
                  />
                ) : (
                  <Checkbox
                    size="small"
                    checked={selected}
                    sx={{
                      p: s('none'),
                      m: s('none'),
                      mr: s('xs'),
                      '& .MuiSvgIcon-root': { fontSize: '1.1rem' },
                    }}
                  />
                )}
                <Text role="body" component="span">
                  {option.label}
                </Text>
              </Box>
            );
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              size="small"
              inputHeight={controlHeight}
              placeholder={!hasValue ? emptyLabel : undefined}
              // eslint-disable-next-line @typescript-eslint/no-deprecated -- Autocomplete renderInput params supply inputProps with required refs/handlers.
              inputProps={{
                ...params.inputProps,
                'aria-label': inputAriaLabel ?? label,
                readOnly: true,
                style: { cursor: 'pointer' },
              }}
            />
          )}
          sx={(theme) => ({
            ...(typeof filterInputSx === 'function' ? filterInputSx(theme) : {}),
            ...heightSx,
            width: '100%',
            minWidth: 0,
            '& .MuiInputBase-root, & .MuiOutlinedInput-root, & .MuiAutocomplete-inputRoot': {
              ...theme.typography.body1,
              fontSize: '14px',
              cursor: 'pointer',
            },
            '& .MuiInputBase-input, & .MuiAutocomplete-input, & input': {
              ...theme.typography.body1,
              fontSize: '14px',
              cursor: 'pointer',
            },
            '& .MuiInputBase-input::placeholder, & .MuiAutocomplete-input::placeholder, & input::placeholder':
              {
                ...theme.typography.body1,
                fontSize: '14px',
                color: theme.palette.text.secondary,
                opacity: 1,
              },
          })}
          slotProps={{
            popper: {
              placement: popperPlacement,
              modifiers: [
                {
                  name: 'flip',
                  options: {
                    fallbackPlacements:
                      popperPlacement === 'bottom-start'
                        ? ['bottom-end', 'top-start', 'top-end']
                        : ['bottom-start', 'top-end', 'top-start'],
                  },
                },
                {
                  name: 'preventOverflow',
                  options: {
                    padding: 16,
                    altAxis: true,
                  },
                },
              ],
              sx: {
                width: 'max-content !important',
                minWidth: `${controlWidthValuePx}px !important`,
                maxWidth: 'min(640px, calc(100vw - 32px)) !important',
                '& .MuiAutocomplete-paper': {
                  minWidth: '100%',
                  width: '100%',
                  maxWidth: '100%',
                },
              },
            },
            paper: {
              sx: {
                backgroundColor: 'background.paper',
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: `${r('md')} !important`,
                overflow: 'hidden',
                boxShadow: 'none',
                minWidth: '100%',
                width: '100%',
                maxWidth: '100%',
              },
            },
            clearIndicator: {
              sx: {
                margin: `${s('none')} !important`,
                marginRight: `${s('none')} !important`,
                padding: `${s('xxs')} !important`,
                width: 'auto',
                height: 'auto',
                opacity: 1,
                visibility: 'visible',
                pointerEvents: 'auto',
                color: 'common.white',
              },
            },
            listbox: {
              sx: {
                py: s('none'),
                minWidth: resolvedControlWidth,
                '& .MuiAutocomplete-option': {
                  paddingLeft: `${optionHorizontalPadding} !important`,
                  paddingRight: `${optionHorizontalPadding} !important`,
                  whiteSpace: 'nowrap',
                },
              },
            },
          }}
          renderValue={(selected) => {
            const selectedValues = Array.isArray(selected) ? selected : [];
            if (selectedValues.length === 0) return null;
            return (
              <Box
                component="span"
                sx={{
                  display: 'inline-block',
                  minWidth: 0,
                  maxWidth: '100%',
                  pl: s('xs'),
                  pr: s('none'),
                  color: 'text.primary',
                  font: 'inherit',
                  fontSize: 'inherit',
                  fontWeight: 'inherit',
                  lineHeight: `${controlHeight}px`,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {selectedValues.map((option) => option.label).join(', ')}
              </Box>
            );
          }}
        />
      </Box>
    </Box>
  );
};

export type { TagOption, TagsFilterFieldProps };
